import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import '../../styles/AdminModal.css';
import '../../styles/AdminBlogModal.css';
import { addBlogPost } from '../../services/AdminBlogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import BlogPostForm from '../../forms/blog/BlogPostForm';

const BlogPostAddModal = forwardRef(({ isOpen, onClose, onSuccess, categories }, ref) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    main_image: null,
    category_id: '',
    status: 'draft',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [contentImages, setContentImages] = useState([]);
  const formRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        content: '',
        main_image: null,
        category_id: '',
        status: 'draft',
      });
      setErrors({});
      setContentImages([]);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'main_image') {
      const file = files[0];
      setFormData(prevFormData => ({
        ...prevFormData,
        main_image: file,
      }));
    }
  };

  const handleContentImagesChange = (images) => {
    setContentImages(images);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }

    // Validar contenido - puede ser string o array JSON
    if (Array.isArray(formData.content)) {
      // Si es array JSON, verificar que tenga al menos un bloque
      if (formData.content.length === 0) {
        setErrors({ content: 'Content is required' });
        return;
      }
      // Verificar que al menos un bloque tenga contenido
      const hasContent = formData.content.some(block => 
        block.type === 'text' && block.content && block.content.trim()
      );
      if (!hasContent) {
        setErrors({ content: 'Content must have at least some text' });
        return;
      }
    } else if (typeof formData.content === 'string') {
      // Si es string, verificar que no esté vacío
      if (!formData.content.trim()) {
        setErrors({ content: 'Content is required' });
        return;
      }
    } else {
      setErrors({ content: 'Content is required' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const formDataToSend = new FormData();
      
      // Add basic form fields
      formDataToSend.append('title', formData.title);
      
      // Convertir contenido a JSON string si es un array
      let contentToSend = formData.content;
      if (Array.isArray(formData.content)) {
        contentToSend = JSON.stringify(formData.content);
      } else {
      }
      formDataToSend.append('content', contentToSend);
      
      formDataToSend.append('category_id', formData.category_id || '');
      formDataToSend.append('status', formData.status);

      // Add main image
      if (formData.main_image && formData.main_image instanceof File) {
        formDataToSend.append('main_image', formData.main_image);
      }

      // Add content images
      if (contentImages.length > 0) {
        contentImages.forEach((imageData, index) => {
          if (imageData.file instanceof File) {
            formDataToSend.append('content_images', imageData.file);
          }
        });
      }

      await addBlogPost(formDataToSend);
      onSuccess();
    } catch (error) {
      console.error('Error adding blog post:', error);
      setErrors({ general: 'Failed to add blog post. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Exponer funciones al componente padre
  useImperativeHandle(ref, () => ({
    getContentImages: () => {
      if (formRef.current) {
        return formRef.current.getContentImages();
      }
      return [];
    }
  }));

  if (!isOpen) return null;

  return (
    <div className="admin__modal">
      <div className="admin__modal-content">
        <div className="admin__modal-intro">
          <h2>Add Blog Post</h2>
          <button className="admin__modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        {errors.general && (
          <div className="error-message">{errors.general}</div>
        )}

        <div className="admin__modal-body blog">
          <BlogPostForm
            ref={formRef}
            formData={formData}
            errors={errors}
            categories={categories}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            onContentImagesChange={handleContentImagesChange}
          />
        </div>

        <div className="admin__modal-buttons">
          <button className="button secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="button" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
});

BlogPostAddModal.displayName = 'BlogPostAddModal';

export default BlogPostAddModal; 